import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { addMonths } from 'date-fns';
import { SurgeryRow, CostCategoryKey, PaymentMethod } from '@/types/budget';

// Calculate effective value considering "Programada" +20% markup
const getEffectiveValue = (value: number, paymentMethod: PaymentMethod): number => {
  return paymentMethod === "Programada" ? value * 1.2 : value;
};

export interface BudgetTransactionOptions {
  paymentMethod: 'cash' | 'installments';
  installments?: number;
  firstPaymentDate: string;
}

export interface PostOpItem {
  id: string;
  description: string;
  quantity: number | null;
  unit_price: number;
  total: number;
}

export interface CreatedTransaction {
  id: string;
  description: string;
  amount: number;
  patient_name: string;
  category: string;
  is_internal_revenue: boolean;
}

export interface CreateBudgetTransactionsResult {
  success: boolean;
  transactions: CreatedTransaction[];
}

/**
 * Creates financial transactions for an approved budget
 * This is a standalone utility function that can be called from async contexts
 */
export async function createBudgetTransactions(
  userId: string,
  organizationId: string,
  budgetId: string,
  budgetTotal: number,
  patientName: string,
  options: BudgetTransactionOptions,
  surgeries?: SurgeryRow[],
  postOpItems?: PostOpItem[]
): Promise<CreateBudgetTransactionsResult> {
  console.log('💰 createBudgetTransactions CALLED with:', {
    userId,
    organizationId,
    budgetId,
    budgetTotal,
    patientName,
    options,
    surgeriesCount: surgeries?.length,
    postOpItemsCount: postOpItems?.length
  });

  if (budgetTotal <= 0) {
    console.log('⚠️ Skipping transaction creation: budget total is 0 or negative');
    return { success: true, transactions: [] };
  }

  try {
    // Check if transactions already exist for this budget
    console.log('🔍 Checking for existing transactions for budget:', budgetId);
    const { data: existing, error: checkError } = await supabase
      .from('transactions')
      .select('id, description, amount, patient_name, category, is_internal_revenue')
      .eq('budget_id', budgetId);

    if (checkError) throw checkError;

    if (existing && existing.length > 0) {
      console.log('⚠️ Transactions already exist for this budget, returning existing');
      toast.info('Transações já existem para este orçamento');
      return { 
        success: true, 
        transactions: existing.map(t => ({
          id: t.id,
          description: t.description,
          amount: t.amount,
          patient_name: t.patient_name || patientName,
          category: t.category,
          is_internal_revenue: t.is_internal_revenue,
        }))
      };
    }

    const categoryLabels: Record<CostCategoryKey, string> = {
      hospital: 'Hospital',
      anesthetist: 'Anestesista',
      prosthesis: 'Prótese',
      team: 'Equipe Médica',
    };

    // If surgeries are provided, create transactions per category with proper isRevenue flags
    if (surgeries && surgeries.length > 0) {
      const transactions: any[] = [];
      
      for (const surgery of surgeries) {
        const categories: CostCategoryKey[] = ['hospital', 'anesthetist', 'prosthesis', 'team'];
        
        for (const categoryKey of categories) {
          const costColumn = surgery[categoryKey];
          if (costColumn.value <= 0) continue;
          
          const isRevenue = costColumn.isRevenue ?? (categoryKey === 'team');
          const categoryLabel = categoryLabels[categoryKey];
          
          // Calculate effective value (with +20% for Programada)
          const effectiveValue = getEffectiveValue(costColumn.value, costColumn.paymentMethod as PaymentMethod);
          
          // Always create a SINGLE transaction per category (installments are informational only)
          let paymentMethodDisplay = 'À vista';
          if (costColumn.paymentMethod === 'Parcelado' && costColumn.installments) {
            paymentMethodDisplay = `${costColumn.installments}x`;
          } else if (costColumn.paymentMethod === 'Programada' && costColumn.installments) {
            paymentMethodDisplay = `Programada ${costColumn.installments}x`;
          } else if (costColumn.paymentMethod === 'Programada') {
            paymentMethodDisplay = 'Programada';
          }
          
          transactions.push({
            user_id: userId,
            organization_id: organizationId,
            budget_id: budgetId,
            type: 'receita',
            category: categoryLabel,
            description: `${surgery.name} - ${categoryLabel} - ${patientName}${costColumn.paymentMethod === 'Programada' ? ' (+20%)' : ''}`,
            amount: effectiveValue,
            date: options.firstPaymentDate,
            due_date: options.firstPaymentDate,
            patient_name: patientName,
            payment_method: paymentMethodDisplay,
            status: 'pending',
            is_internal_revenue: isRevenue,
            cost_category: categoryKey,
          });
        }
      }
      
      // Add post-op items transaction (always internal revenue)
      if (postOpItems && postOpItems.length > 0) {
        const postOpTotal = postOpItems.reduce((sum, item) => sum + (item.total || 0), 0);
        
        if (postOpTotal > 0) {
          transactions.push({
            user_id: userId,
            organization_id: organizationId,
            budget_id: budgetId,
            type: 'receita',
            category: 'Pós-Operatório',
            description: `Pós-Operatório - ${patientName}`,
            amount: postOpTotal,
            date: options.firstPaymentDate,
            due_date: options.firstPaymentDate,
            patient_name: patientName,
            payment_method: 'À vista',
            status: 'pending',
            is_internal_revenue: true,
          });
        }
      }
      
      if (transactions.length > 0) {
        const { data: inserted, error } = await supabase
          .from('transactions')
          .insert(transactions)
          .select('id, description, amount, patient_name, category, is_internal_revenue');

        if (error) throw error;
        console.log(`✅ Created ${transactions.length} transactions from surgeries and post-op items`);
        
        toast.success('Transações financeiras geradas com sucesso!');
        return {
          success: true,
          transactions: (inserted || []).map(t => ({
            id: t.id,
            description: t.description,
            amount: t.amount,
            patient_name: t.patient_name || patientName,
            category: t.category,
            is_internal_revenue: t.is_internal_revenue,
          })),
        };
      }
      
      return { success: true, transactions: [] };
    } else {
      // Fallback: create single transaction for total (legacy behavior)
      if (options.paymentMethod === 'cash') {
        console.log('💵 Creating cash payment transaction');
        
        const { data: inserted, error } = await supabase
          .from('transactions')
          .insert({
            user_id: userId,
            organization_id: organizationId,
            budget_id: budgetId,
            type: 'receita',
            category: 'Procedimento',
            description: `Orçamento ${budgetId.slice(0, 8)} - ${patientName}`,
            amount: budgetTotal,
            date: options.firstPaymentDate,
            due_date: options.firstPaymentDate,
            patient_name: patientName,
            payment_method: 'À vista',
            status: 'pending',
            is_internal_revenue: true,
          })
          .select('id, description, amount, patient_name, category, is_internal_revenue');

        if (error) throw error;
        console.log('✅ Cash transaction created successfully');
        
        toast.success('Transações financeiras geradas com sucesso!');
        return {
          success: true,
          transactions: (inserted || []).map(t => ({
            id: t.id,
            description: t.description,
            amount: t.amount,
            patient_name: t.patient_name || patientName,
            category: t.category,
            is_internal_revenue: t.is_internal_revenue,
          })),
        };
        
      } else if (options.installments && options.installments > 1) {
        console.log(`💳 Creating ${options.installments} installment transactions`);
        
        const installmentAmount = budgetTotal / options.installments;
        const transactions = [];

        for (let i = 1; i <= options.installments; i++) {
          const dueDate = addMonths(new Date(options.firstPaymentDate), i - 1)
            .toISOString()
            .split('T')[0];

          transactions.push({
            user_id: userId,
            organization_id: organizationId,
            budget_id: budgetId,
            type: 'receita',
            category: 'Procedimento',
            description: `Orçamento ${budgetId.slice(0, 8)} - ${patientName} (Parcela ${i}/${options.installments})`,
            amount: installmentAmount,
            date: dueDate,
            due_date: dueDate,
            patient_name: patientName,
            payment_method: `${options.installments}x`,
            status: 'pending',
            installment_number: i,
            total_installments: options.installments,
            is_internal_revenue: true,
          });
        }

        const { data: inserted, error } = await supabase
          .from('transactions')
          .insert(transactions)
          .select('id, description, amount, patient_name, category, is_internal_revenue');

        if (error) throw error;
        console.log('✅ All installment transactions created successfully');
        
        toast.success('Transações financeiras geradas com sucesso!');
        return {
          success: true,
          transactions: (inserted || []).map(t => ({
            id: t.id,
            description: t.description,
            amount: t.amount,
            patient_name: t.patient_name || patientName,
            category: t.category,
            is_internal_revenue: t.is_internal_revenue,
          })),
        };
      }
    }

    return { success: true, transactions: [] };
  } catch (error: any) {
    console.error('❌ Error creating budget transactions:', error);
    toast.error('Erro ao criar transações financeiras');
    return { success: false, transactions: [] };
  }
}

/**
 * Updates the amount_paid field of a budget based on paid transactions
 */
export async function updateBudgetAmountPaid(budgetId: string) {
  console.log('💵 Updating amount_paid for budget:', budgetId);

  try {
    // Get all paid transactions for this budget
    const { data: paidTransactions, error: fetchError } = await supabase
      .from('transactions')
      .select('amount')
      .eq('budget_id', budgetId)
      .eq('type', 'receita')
      .eq('status', 'paid');

    if (fetchError) throw fetchError;

    // Calculate total amount paid
    const totalPaid = (paidTransactions || []).reduce(
      (sum, t) => sum + (t.amount || 0),
      0
    );

    console.log('💰 Total paid:', totalPaid);

    // Update budget amount_paid
    const { error: updateError } = await supabase
      .from('budgets')
      .update({ amount_paid: totalPaid })
      .eq('id', budgetId);

    if (updateError) throw updateError;

    console.log('✅ Budget amount_paid updated successfully');
  } catch (error: any) {
    console.error('❌ Error updating budget amount_paid:', error);
    // Don't throw - this is a background update
  }
}
